import {createContext, useContext, useMemo} from 'react'
import {type StyleProp, type TextStyle, type ViewStyle} from 'react-native'

import {
  directionTextStyle,
  type FlashBookDirection,
} from '#/lib/flashbookDirection'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {
  DEFAULT_FLASHBOOK_DESIGN,
  designInHand,
  type FlashBookDesign,
} from '#/themes-flashbook/design'
import {useFlashBookHands} from '#/themes-flashbook/hands'
import {type FlashBookThemeTokens} from '#/types/flashbook'

export type FlashBookPlayPalette = {
  page: string
  surface: string
  surfaceAlt: string
  border: string
  text: string
  body: string
  muted: string
  explanation: string
  inputBg: string
  inputText: string
  accent: string
  positive: string
  negative: string
  warning: string
  onAccent: string
  // ── Shell ink ─────────────────────────────────────────────────────────────
  //
  // A book is a page *inside* a shell: the running head, the page number, the
  // way out, the report header and an art-less cover all sit on `tokens.bg`, not
  // on the paper. On a light-paper theme the page ink is dark brown/indigo and
  // the shell is near-black, so reusing `textPrimary` there renders dark-on-dark
  // — invisible. These three are the ink for everything that sits on the shell.
  chromeText: string
  chromeMuted: string
  chromeBorder: string
  // ── Page furniture on the paper ───────────────────────────────────────────
  //
  // Play mode wears the theme across the *whole* screen, not only the blocks:
  // the running head, the page progress and the page navigation are printed on
  // the theme's own paper, in the theme's own ink. These are the few values the
  // paper tokens cannot supply on their own.
  /** The unfilled part of a progress measure drawn on the page. */
  paperTrack: string
  /** A hairline printed on the page (header rule, chip edge). */
  paperHairline: string
}

const defaultPalette: FlashBookPlayPalette = {
  page: PLAY_SURFACE.page,
  surface: PLAY_SURFACE.surface,
  surfaceAlt: '#1b2a3d',
  border: PLAY_SURFACE.border,
  text: PLAY_SURFACE.text,
  body: '#cbd5e1',
  muted: PLAY_SURFACE.textFaint,
  explanation: PLAY_SURFACE.textMuted,
  inputBg: 'rgba(255,255,255,0.06)',
  inputText: '#fff',
  accent: '#0085ff',
  // These three match the literals the shared widgets used before they became
  // theme-aware, so capsule play (no FlashBook theme) looks exactly as it did.
  positive: '#22c55e',
  negative: '#ef4444',
  warning: '#f59e0b',
  onAccent: '#fff',
  chromeText: '#fff',
  chromeMuted: 'rgba(255,255,255,0.72)',
  chromeBorder: 'rgba(255,255,255,0.22)',
  paperTrack: 'rgba(255,255,255,0.16)',
  paperHairline: 'rgba(255,255,255,0.14)',
}

/**
 * Resolves the semantic colors used by shared capsule question widgets.
 * Keeping this palette semantic prevents light FlashBook pages from inheriting
 * the capsule editor's hard-coded white-on-dark colors.
 */

// ─── Contrast helpers ────────────────────────────────────────────────────────

/** Relative luminance of a `#rgb` / `#rrggbb` color (WCAG 2.1). */
function luminance(hex: string): number {
  let h = hex.replace('#', '')
  if (h.length === 3)
    h = h
      .split('')
      .map(c => c + c)
      .join('')
  const [r, g, b] = [0, 2, 4].map(i => {
    const s = parseInt(h.slice(i, i + 2), 16) / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio between two hex colors. */
function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * Keeps `ink` when it is legible on `bg`, otherwise swaps in `fallback`.
 *
 * Used for shell furniture: a theme's own ink is preferred so the book keeps
 * its identity, but never at the cost of reading it.
 */
function readableOn(
  ink: string,
  bg: string,
  fallback: string,
  min: number,
): string {
  if (!HEX.test(ink) || !HEX.test(bg)) return ink
  return contrast(ink, bg) >= min ? ink : fallback
}

/** The ink to put on an accent-filled button. */
const ON_ACCENT_INK = '#111827'

/**
 * Picks the readable label color for a filled accent surface instead of
 * assuming white. A light accent (the magazine theme's cyan, for example) reads
 * far better with dark ink than with white text.
 */
function onAccentFor(accent: string): string {
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(accent)) return '#fff'
  return contrast('#ffffff', accent) >= contrast(ON_ACCENT_INK, accent)
    ? '#fff'
    : ON_ACCENT_INK
}

/**
 * The readable ink for any filled surface.
 *
 * Exported because widgets sometimes fill a shape with a *state* colour rather
 * than the theme accent (a tick on a success pill, a marker on an error row),
 * and hard-coding white would vanish on the light themes' darker tones.
 */
export function inkOn(color: string): string {
  return onAccentFor(color)
}

export function getFlashBookPlayPalette(
  tokens?: FlashBookThemeTokens,
): FlashBookPlayPalette {
  if (!tokens) return defaultPalette

  const light = tokens.isLight
  return {
    page: tokens.pageBg,
    surface: tokens.cardBg,
    // On paper themes a translucent wash belongs to the page instead of a
    // second, alien white/blue panel stacked on top of it.
    surfaceAlt: light
      ? 'rgba(0,0,0,0.045)'
      : (tokens.bgSecondary ?? tokens.cardBg),
    border: tokens.cardBorder,
    text: tokens.textPrimary,
    body: tokens.textSecondary,
    muted: tokens.textMuted,
    explanation: tokens.textSecondary,
    inputBg: light ? '#ffffff' : (tokens.bgSecondary ?? tokens.cardBg),
    inputText: tokens.textPrimary,
    accent: tokens.accent,
    positive: light ? '#15803d' : '#4ade80',
    negative: light ? '#b91c1c' : '#fb7185',
    warning: light ? '#b45309' : '#fbbf24',
    // Never assume white: on a light accent (magazine's cyan) dark ink is the
    // readable choice.
    onAccent: onAccentFor(tokens.accent),
    // The paper themes keep their warm/cool ink on the shell only if it is
    // actually legible there; otherwise the shell falls back to light ink.
    chromeText: readableOn(tokens.textPrimary, tokens.bg, '#ffffff', 4.5),
    chromeMuted: readableOn(
      tokens.textSecondary,
      tokens.bg,
      'rgba(255,255,255,0.72)',
      3,
    ),
    chromeBorder: readableOn(
      tokens.cardBorder,
      tokens.bg,
      'rgba(255,255,255,0.22)',
      1.6,
    ),
    // The theme's `progressTrack` is tuned for the dark shell; printed on light
    // paper it would be white-on-white and the measure would vanish.
    paperTrack: light ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.16)',
    paperHairline: light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.14)',
  }
}

const FlashBookPlayThemeContext = createContext<
  FlashBookThemeTokens | undefined
>(undefined)

/**
 * A context that lets a non-book surface (e.g. a document question card) inject
 * a fully-computed palette without needing to supply `FlashBookThemeTokens`.
 * `useFlashBookPlayPalette` checks this first, so it wins over the token-derived
 * palette when present.
 */
const FlashBookPlayPaletteContext = createContext<
  FlashBookPlayPalette | undefined
>(undefined)

const FlashBookPlayDesignContext = createContext<FlashBookDesign | undefined>(
  undefined,
)

/**
 * Which way the writing in this subtree runs.
 *
 * Set per *block*, because that is the smallest thing a creator writes: a page
 * of an Arabic exercise book can hold an English quotation, and the quotation
 * must not be laid out right-to-left just because the page around it is. The
 * page's own direction is provided above the blocks so that furniture and
 * text-less blocks have something to fall back to.
 */
const FlashBookPlayDirectionContext = createContext<
  FlashBookDirection | undefined
>(undefined)

export function FlashBookPlayThemeProvider({
  tokens,
  design,
  direction,
  children,
}: React.PropsWithChildren<{
  tokens: FlashBookThemeTokens
  design?: FlashBookDesign
  /**
   * The direction this subtree is written in. Omitted means “inherit”, which is
   * what keeps a nested provider from flattening an outer one to `ltr`.
   */
  direction?: FlashBookDirection
}>) {
  /**
   * The book's hands, fetched the first time a book is rendered.
   *
   * This provider is the one place every page of every book passes through —
   * the reader, the studio's preview and the theme selector's miniature — so it
   * is where the faces a theme may be written in are asked for. Nothing waits on
   * the answer: the stack a theme names already falls back to the machine's own
   * hand, so a late arrival is a slightly different handwriting, never a blank.
   */
  useFlashBookHands()

  /**
   * The direction this subtree will actually print in.
   *
   * Read from the enclosing provider rather than defaulted to `ltr`, so a
   * provider that was given no direction of its own inherits the one around it
   * instead of silently turning a right-to-left page into an English layout.
   * The fallback is the module default, which is only reached outside a book.
   */
  const inherited = useContext(FlashBookPlayDirectionContext)
  const resolvedDirection = direction ?? inherited

  /**
   * The design, in the hand this subtree is written in.
   *
   * A theme names one face per written role, and on a device a face is a single
   * family with no per-glyph fallback — so the role the page prints has to name
   * the script it is about to print. Re-resolving *here*, at the one seam every
   * page of every book passes through, is what makes that true for every
   * consumer at once: the shared question widgets and every reading block read
   * this context, and none of them has to know that a direction can change a
   * family. The reader's own furniture is not inside a block and is resolved for
   * itself, from the direction it prints in. On the web it is the same object it
   * was given (see `designInHand`), so nothing about an English book changes.
   */
  const designInDirection = useMemo(
    () => (design ? designInHand(design, resolvedDirection) : undefined),
    [design, resolvedDirection],
  )

  return (
    <FlashBookPlayThemeContext.Provider value={tokens}>
      <FlashBookPlayDesignContext.Provider value={designInDirection}>
        <FlashBookPlayDirectionContext.Provider value={resolvedDirection}>
          {children}
        </FlashBookPlayDirectionContext.Provider>
      </FlashBookPlayDesignContext.Provider>
    </FlashBookPlayThemeContext.Provider>
  )
}

/**
 * The direction the writing under this point of the tree runs in.
 *
 * Outside a book this is `ltr`, so capsule play and the capsule studio are
 * untouched: direction is a property of a *book*, not of the app.
 */
export function useFlashBookPlayDirection(): FlashBookDirection {
  return useContext(FlashBookPlayDirectionContext) ?? 'ltr'
}

/** Whether the writing here runs right-to-left. */
export function useIsFlashBookRtl(): boolean {
  return useFlashBookPlayDirection() === 'rtl'
}

/**
 * The layout half of a direction: how a *box* is laid out in this subtree.
 *
 * `direction` is the one property that does the most work here. On the web it
 * is the CSS property, so it mirrors a row's children, the flow of inline text
 * inside them and anything using logical edges; in Yoga (native) it does the
 * same. That is the difference between a book that is *laid out* right-to-left
 * and an English layout with its paragraphs right-aligned.
 *
 * A book that already runs `ltr` gets an empty object rather than an explicit
 * `ltr`, so nothing about an English book's layout changes.
 */
export function usePlayDirectionStyle(): ViewStyle {
  const inBook = useIsFlashBookPlay()
  const rtl = useIsFlashBookRtl()
  return useMemo(() => (inBook && rtl ? {direction: 'rtl'} : {}), [inBook, rtl])
}

/**
 * The text half of a direction: how a *line* is set in this subtree.
 *
 * Web and Yoga inherit the box direction, but a text run on iOS reads
 * `writingDirection` and one on Android reads `textAlign`, and neither derives
 * it from the box — so a role that prints reading text has to say it.
 */
export function usePlayDirectionTextStyle(): TextStyle {
  const inBook = useIsFlashBookPlay()
  const direction = useFlashBookPlayDirection()
  return useMemo(
    () => (inBook ? directionTextStyle(direction) : {}),
    [inBook, direction],
  )
}

export function useFlashBookPlayTheme() {
  return useContext(FlashBookPlayThemeContext)
}

/**
 * The book's design language for the widget being rendered.
 *
 * Outside a FlashBook this is the neutral default, so capsule play keeps its
 * existing look: open blocks, soft answer rows, no page furniture.
 */
export function useFlashBookPlayDesign(): FlashBookDesign {
  return useContext(FlashBookPlayDesignContext) ?? DEFAULT_FLASHBOOK_DESIGN
}

/**
 * Whether a shared widget is currently rendering inside a FlashBook.
 *
 * Widgets use this to tell "I am a page in a book" apart from "I am the whole
 * screen in capsule play", so book-adapted layout can never leak into the
 * capsule flow that has to keep looking exactly as it did.
 */
export function useIsFlashBookPlay(): boolean {
  return useContext(FlashBookPlayThemeContext) !== undefined
}

/**
 * Whether a shared widget should skip its own framing surface.
 *
 * Inside a FlashBook the *block* owns the surface: `panel` / `inset` themes
 * draw the block's frame, while `open` / `ruled` themes print straight onto the
 * paper. A widget drawing a box of its own would therefore either nest a panel
 * inside a panel (the "boxes inside boxes" a page must never show) or float a
 * panel on bare paper — either way the page stops reading as one book.
 *
 * Outside a book the widget keeps the surface it always had, which is what
 * capsule play renders against.
 */
export function usePlaySuppressOwnSurface(): boolean {
  return useIsFlashBookPlay()
}

/**
 * Horizontal/vertical padding for a widget's content.
 *
 * Inside a book the page already provides its own margins, so a widget adding
 * its own 24px would nest padding inside padding — the visual "box inside a
 * box" that makes a book read as a stack of app panels. Outside a book the
 * widget's original value is preserved untouched.
 */
export function usePlayContentPadding(fallback: number): number {
  const inBook = useIsFlashBookPlay()
  const d = useFlashBookPlayDesign()
  if (!inBook) return fallback
  // On paper with lines, the page's own margin *is* the margin — and it is the
  // margin the ruling was cut to. A widget adding a second inset of its own
  // would start its first line part-way down a rule, so from the second line of
  // the exercise onwards the writing no longer sits on the lines. Zero here is
  // what keeps a question and its answers in register with the paper.
  return d.block.mode === 'ruled' ? 0 : d.page.padding
}

/** Average glyph width of the app's UI font, as a fraction of font size. */
const AVG_GLYPH = 0.58
/** Horizontal breathing room inside an inline blank, per side. */
const BLANK_INSET = 12

/**
 * The width of an inline answer field.
 *
 * The field is a writing space, so its size is a *hint about the answer*: the
 * learner can see how long the missing word is. It therefore follows the
 * longest accepted answer ("the correct answer, or a bit larger"), measured in
 * the field's own font size so a theme that prints with larger type gets
 * correspondingly wider fields instead of one fixed number of pixels per
 * character.
 *
 * Outside a book this returns the caller's own measurement untouched, so the
 * capsule editor keeps the exact geometry it had.
 */
export function usePlayBlankWidth(
  answer: string | undefined,
  alternatives: string[] | undefined,
  fontSize: number,
  /** The width a caller used before fields became measurable. */
  fallback: number,
  min = 48,
): number {
  const inBook = useIsFlashBookPlay()
  return useMemo(() => {
    if (!inBook) return fallback
    const longest = [answer ?? '', ...(alternatives ?? [])].reduce(
      (a, b) => (b.length > a.length ? b : a),
      '',
    )
    if (!longest) return min
    // One extra character of slack: the space reads as a blank to fill rather
    // than a box that has to be hit exactly.
    const chars = longest.length + 0.6
    return Math.round(chars * fontSize * AVG_GLYPH + BLANK_INSET * 2)
  }, [answer, alternatives, fontSize, fallback, inBook, min])
}

/**
 * The container style for an answer option, resolved from the theme's quiz
 * design.
 *
 * This is the single place that decides whether an option looks like a bordered
 * card, a ruled exercise line, a soft tinted row or an editorial slab. Widgets
 * only say "this is an option"; the theme decides what that means. Selected /
 * correct / wrong states are layered on top by the caller because only it knows
 * the answer state.
 */
export function usePlayOptionStyle(): ViewStyle {
  const d = useFlashBookPlayDesign()
  const p = useFlashBookPlayPalette()
  const direction = usePlayDirectionStyle()
  const rtl = direction.direction === 'rtl'
  const {option, optionRadius, optionBorderWidth, optionPadding} = d.quiz

  return useMemo(() => {
    const base: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      padding: optionPadding,
      borderRadius: optionRadius,
    }

    switch (option) {
      case 'row':
        return {
          ...base,
          borderRadius: Math.min(optionRadius, 6),
          borderWidth: optionBorderWidth,
          borderColor: p.border,
          backgroundColor: 'transparent',
        }
      case 'slab':
        return {
          ...base,
          borderRadius: optionRadius,
          borderWidth: 0,
          // The editorial spine is the *leading* edge, and in a book written
          // right-to-left the leading edge is the right one. A physical
          // `borderLeftWidth` is not flipped by the box direction, so this is
          // the one place where mirroring is stated rather than inherited.
          ...(rtl
            ? {borderRightWidth: 4, borderRightColor: p.accent}
            : {borderLeftWidth: 4, borderLeftColor: p.accent}),
          backgroundColor: p.surface,
        }
      case 'card':
        return {
          ...base,
          borderWidth: optionBorderWidth,
          borderColor: p.border,
          backgroundColor: p.surface,
        }
      case 'soft':
      default:
        return {
          ...base,
          borderWidth: optionBorderWidth,
          borderColor: p.border,
          backgroundColor: p.surfaceAlt,
        }
    }
  }, [
    option,
    optionBorderWidth,
    optionPadding,
    optionRadius,
    p.accent,
    p.border,
    p.surface,
    p.surfaceAlt,
    rtl,
  ])
}

/**
 * The type an answer is written in.
 *
 * An exercise book does not print its answers in a different hand from its
 * questions, so the theme's own label role is applied to the answer text too.
 * This is the one place that decides it, because a widget that hard-coded its
 * own label style would be the single thing on the page still in the app's UI
 * font — which is exactly how a theme ends up looking half-applied.
 *
 * Deliberately narrow: it carries the theme's *face* (only themes that name one
 * declare it) and, on a ruled page, the line height that puts the answer on a
 * line. Font size, weight and colour stay with the widget, so no theme can
 * silently reflow another one's rows.
 */
/**
 * One of the book's own type roles, for a widget that prints reading text.
 *
 * Returns nothing outside a book, so capsule play and the capsule studio keep
 * the exact styles they had: the theme's typography is only ever applied to
 * text that is actually printed on a page.
 */
export function usePlayTextStyle(
  role: keyof FlashBookDesign['type'],
): TextStyle {
  const inBook = useIsFlashBookPlay()
  const d = useFlashBookPlayDesign()
  const direction = usePlayDirectionTextStyle()
  return useMemo(
    () => (inBook ? {...d.type[role], ...direction} : {}),
    [inBook, d, role, direction],
  )
}

export function usePlayOptionTextStyle(): TextStyle {
  const d = useFlashBookPlayDesign()
  const face = d.type.label.fontFamily
  const onTheRule = d.block.mode === 'ruled'
  const direction = usePlayDirectionTextStyle()

  return useMemo(() => {
    const style: TextStyle = {...direction}
    if (face) style.fontFamily = face
    if (onTheRule) style.lineHeight = d.type.label.lineHeight
    return style
  }, [face, onTheRule, d.type.label.lineHeight, direction])
}

/**
 * A hex colour at an alpha, as `rgba()`.
 *
 * Alpha is what makes a highlighter a highlighter: the paper's own writing has
 * to stay readable *through* the stroke, so a mark is never opaque ink.
 */
function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const full =
    h.length === 3
      ? h
          .split('')
          .map(c => c + c)
          .join('')
      : h
  if (full.length !== 6) return hex
  const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * How the active theme marks a chosen answer: a filled row, or a stroke of ink
 * over the words themselves.
 */
export function usePlaySelectionMode(): 'tint' | 'highlight' {
  return useFlashBookPlayDesign().quiz.selection ?? 'tint'
}

/**
 * The highlighter stroke for one answer, or nothing at all.
 *
 * Returned as a function of the *state colour* the widget has already resolved
 * for its marker, so the stroke and the tick beside it can never disagree:
 * success ink for a right answer, error ink for a wrong one, and the theme's own
 * marker colour (a warm amber in a classroom book) for a choice that has not
 * been judged yet.
 *
 * Pass `null` for a row that is not marked at all — an unchosen option, or an
 * answer the learner has already judged. Marking every row would be as
 * meaningless as highlighting a whole page, and on paper it looks exactly as
 * wrong as it sounds.
 *
 * The style goes on the *text*, not on the row: a `Text` background paints the
 * glyph box per line, so a wrapped answer is underlined line by line the way a
 * hand-held marker does it, instead of filling a rectangle the width of the
 * page.
 *
 * Empty for every theme that marks by filling the row, so those keep the look
 * they already had.
 */
export function useInkStroke(): (
  tone: string | null | undefined,
) => TextStyle | undefined {
  const d = useFlashBookPlayDesign()
  const p = useFlashBookPlayPalette()
  const mode = d.quiz.selection ?? 'tint'
  const chosenInk = d.quiz.highlightInk ?? p.accent

  // Deliberately not memoized, like `usePlayRuleSnap`: the result is a pure
  // producer of styles that is only ever called during render, so there is no
  // identity worth preserving — and the compiler cannot hoist a memo that
  // returns a fresh closure anyway.
  if (mode !== 'highlight') return () => undefined

  return (tone: string | null | undefined) => {
    if (!tone) return undefined
    const ink =
      tone === p.positive
        ? p.positive
        : tone === p.negative
          ? p.negative
          : tone === p.warning
            ? p.warning
            : tone === p.muted
              ? null
              : chosenInk
    if (!ink) return undefined
    return {
      backgroundColor: withAlpha(ink, 0.42),
      borderRadius: 2,
      // A real marker overshoots: the nib is wider than the letter, so the
      // stroke runs a little past both ends of the word. Inline padding gives
      // exactly that — it widens the painted box without moving the text.
      paddingHorizontal: 3,
    }
  }
}

/**
 * The page's ruling pitch, or 0 when the theme does not print on lines.
 *
 * This is the number every vertical measure on a ruled page has to answer to.
 * A ruled page is not a background: it is the paper the exercise is written on,
 * and a block that adds 14px of its own above its first line has pushed every
 * line of that exercise half a rule off the paper for the rest of the page.
 */
export function usePlayRuling(): number {
  const d = useFlashBookPlayDesign()
  if (d.block.mode !== 'ruled') return 0
  return d.page.textureStep ?? d.type.body.lineHeight
}

/**
 * Rounds a vertical measure up to a whole number of rules.
 *
 * Meant for the things a theme cannot express as a type role — a control's
 * height, the space between two items in a list, a grid cell. On a ruled page
 * anything in the vertical stack that is not a whole rule shifts everything
 * beneath it off the ruling; off one, this is the identity function and no
 * layout changes at all.
 */
export function usePlayRuleSnap(): (height: number) => number {
  const step = usePlayRuling()
  // Deliberately not memoized: the result is a pure rounding function that is
  // only ever called to compute a number during render, so there is nothing to
  // preserve identity for.
  return step > 0 ? (h: number) => Math.ceil(h / step) * step : (h: number) => h
}

/**
 * A control's height on ruled paper, as a whole number of rules.
 *
 * A button is not writing, so it does not need to sit *on* a line — but on a
 * ruled page it must still *end* on one, or everything stacked after it (the
 * explanation, the retry, the next part of the exercise) is off the ruling.
 * Empty off a ruled page, so every other theme keeps its own button size.
 */
export function usePlayControlStyle(baseHeight: number): ViewStyle {
  const step = usePlayRuling()
  const snap = usePlayRuleSnap()
  const radius = useFlashBookPlayDesign().quiz.optionRadius
  return useMemo(
    () =>
      step > 0
        ? // Squared off to the theme's own option radius: a 14px pill is the
          // app's language, and on a ruled page an action should be stamped
          // rather than floated.
          {minHeight: snap(baseHeight), borderRadius: radius}
        : {},
    [step, snap, baseHeight, radius],
  )
}

/**
 * A block's primary action — `Check`, `Mark as read` — as the page would have
 * it.
 *
 * Two things are wrong with one generic filled pill on every theme. In a book
 * whose answers are crisp editorial bars, squared index cards or generous
 * rounded panels, the *same* 14px app pill ended every exercise — the theme
 * changed the answers and then stopped at the action. And on a page of ruled
 * paper it is worse than generic: a full-width tinted bar with a pill radius is
 * the most app-shaped thing a schoolbook can carry, and a learner reading an
 * exercise sees a web form below their writing.
 *
 * So inside a book the action takes the theme's own answer shape — radius and
 * accent bar straight from the quiz design, so there is still one place that
 * decides what a shape is in a given theme — and on ruled paper it stops being a
 * button at all: what paper has at the end of an exercise is the teacher's own
 * instruction, written in ink and underlined so you know it is the thing to
 * press.
 *
 * Outside a book — capsule play — it is the identity. Those screens must keep
 * looking exactly as they did.
 *
 * The height stays a whole number of the page's lines either way, so the action
 * never takes the ruling out from under whatever follows it.
 */
export function usePlayPrimaryAction(baseHeight: number): {
  /** Container look, to be applied *after* the widget's own styles. */
  style: StyleProp<ViewStyle>
  /** The action's ink, or `null` to keep the caller's colour. */
  ink: (enabled: boolean) => string | null
  /** Underline for the action's label, or `undefined` to leave it alone. */
  labelStyle: (enabled: boolean) => TextStyle | undefined
} {
  const d = useFlashBookPlayDesign()
  const p = useFlashBookPlayPalette()
  const inBook = useIsFlashBookPlay()
  const control = usePlayControlStyle(baseHeight)
  const printed = d.block.mode === 'ruled'
  const {option: shape, optionRadius} = d.quiz

  return useMemo(() => {
    if (printed) {
      const inkOf = (enabled: boolean) => (enabled ? p.accent : p.muted)
      return {
        style: [control, {backgroundColor: 'transparent', borderRadius: 0}],
        ink: inkOf,
        labelStyle: (enabled: boolean) => ({
          // The underline is what says "press me" once there is no fill left to
          // say it, and it is written in the same ink as the word.
          textDecorationLine: 'underline' as const,
          textDecorationColor: inkOf(enabled),
        }),
      }
    }

    // Outside a book — capsule play — the action stays exactly as it was: the
    // app's own pill. Only a book's action borrows the book's answer language.
    if (!inBook) {
      return {
        style: control,
        ink: () => null,
        labelStyle: () => undefined,
      }
    }

    /**
     * Inside one, it wears the shape the theme's own *answers* wear.
     *
     * A 14px pill is the app's corner, and a book whose answers are crisp
     * editorial bars (Magazine, radius 2), squared index cards (Heritage, 6) or
     * generous rounded panels (Comic, 18) should not end its exercise in the
     * same pill as every other theme. The radius and the theme's own accent
     * bar come from the quiz design, so there is still exactly one place that
     * decides what a shape is in a given theme.
     */
    return {
      style: [
        control,
        {borderRadius: optionRadius},
        shape === 'slab' && {
          borderLeftWidth: 4,
          borderLeftColor: p.accent,
        },
      ],
      ink: () => p.onAccent,
      labelStyle: () => undefined,
    }
  }, [
    printed,
    inBook,
    control,
    p.accent,
    p.muted,
    p.onAccent,
    shape,
    optionRadius,
  ])
}

/**
 * A stack gap of a whole number of rules, so the next element lands on one.
 *
 * A 16px app gap between a question and its answers is sixteen pixels the ruling
 * does not have — from that point on every line of the exercise floats between
 * two lines. Off a ruled page the caller's own gap is returned untouched.
 */
export function usePlayStackGap(fallback: number): number {
  const snap = usePlayRuleSnap()
  return snap(fallback)
}

/**
 * The vertical space an answer row needs on a ruled page.
 *
 * Writing sits *on* a line, not between two: an option on ruled paper is a
 * blank line followed by the line the answer is written on. So the row's own
 * height is a whole number of rules and its text begins on one, which is why
 * this has to come from the theme's pitch rather than from a padding value
 * chosen for a bordered card.
 *
 * Outside a ruled theme it is empty, and every other theme keeps the exact row
 * geometry its quiz design already asked for.
 */
export function usePlayOptionRowRhythm(): ViewStyle {
  const d = useFlashBookPlayDesign()
  const step = d.page.textureStep ?? d.type.body.lineHeight
  const onTheRule = d.block.mode === 'ruled'

  return useMemo(() => {
    if (!onTheRule || step <= 0) return {}
    return {
      // A blank rule above the writing, and the writing's own rule below it.
      paddingTop: step,
      paddingBottom: 0,
      borderRadius: 0,
      borderWidth: 0,
    }
  }, [onTheRule, step])
}

/**
 * The strip of page furniture a block's primary action sits in.
 *
 * On ruled paper the strip goes: a tinted, bordered bar printed across the
 * lines is exactly the "app panel" a book page must never show, and its 20px of
 * padding plus its hairline put the button — and everything measured after it —
 * off the ruling. What is left is the action itself, printed on the paper like
 * the rest of the exercise, separated from it by the block's own whole-rule
 * gap.
 *
 * Empty for every other theme, which keeps the bar it already had.
 */
export function usePlayFooterStyle(): ViewStyle {
  const d = useFlashBookPlayDesign()
  return useMemo(
    () =>
      d.block.mode === 'ruled'
        ? {backgroundColor: 'transparent', borderTopWidth: 0, padding: 0}
        : {},
    [d.block.mode],
  )
}

/**
 * The gap between answer rows.
 *
 * On ruled paper the rows carry their own blank line, so the container must add
 * nothing: a 10px app gap between two rows is ten pixels the ruling does not
 * have, and from the second answer onwards every line of writing drifts off the
 * line it is supposed to be written on.
 *
 * Empty for every other theme, whose quiz design keeps its own gap.
 */
export function usePlayOptionListStyle(): ViewStyle {
  const d = useFlashBookPlayDesign()
  const onTheRule = d.block.mode === 'ruled'
  return useMemo(() => (onTheRule ? {gap: 0, marginTop: 0} : {}), [onTheRule])
}

/**
 * Semantic colors for the shared question widgets currently being rendered.
 * Inside a FlashBook they follow the book theme; anywhere else (capsule play,
 * the capsule studio) they fall back to the dark editor palette, so existing
 * screens keep the exact look they had before.
 */
/**
 * Provide a pre-computed palette directly, bypassing theme tokens.
 *
 * Use this when rendering shared question widgets in an app surface (e.g. a
 * document question card) rather than inside a FlashBook: the widgets already
 * know how to draw themselves, and this just tells them which colours to use.
 */
export function FlashBookPlayPaletteProvider({
  palette,
  children,
}: React.PropsWithChildren<{palette: FlashBookPlayPalette}>) {
  return (
    <FlashBookPlayPaletteContext.Provider value={palette}>
      {children}
    </FlashBookPlayPaletteContext.Provider>
  )
}

export function useFlashBookPlayPalette(): FlashBookPlayPalette {
  const direct = useContext(FlashBookPlayPaletteContext)
  const theme = useContext(FlashBookPlayThemeContext)
  if (direct) return direct
  return getFlashBookPlayPalette(theme)
}

/**
 * The accent a question widget should use for its interactive affordances
 * (selected options, active chips, focus rings).
 *
 * Inside a book every block answers to the book's own accent, so a storybook
 * reads as one design instead of a stack of differently-colored widgets.
 * Outside a book the per-type capsule accent is returned untouched, which keeps
 * capsule play exactly as it was.
 */
export function usePlayAccent(typeAccent: string): string {
  return useContext(FlashBookPlayThemeContext)?.accent ?? typeAccent
}
